import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { Plus, Trash2, UploadCloud } from "lucide-react";
import "./material.css";

export default function EditMaterial() {
  const navigate = useNavigate();
  const { id } = useParams();

  const user = JSON.parse(localStorage.getItem("NagpurProperties") || "{}");

  const canViewPrice =
    user?.role?.some(
      (r) =>
        r.roleName === "Admin" ||
        r.roleName === "SuperAdmin" ||
        r.roleName === "SuperEngineer"
    ) || false;

  const [vendors, setVendors] = useState([]);
  const [sites, setSites] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [vehicalImg, setVehicalImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dmNo, setDmNo] = useState("");

  const [form, setForm] = useState({
    vendorId: "",
    siteId: "",
    vehicleNo: "",
    remark: "",
    engineerName: user?.email || "",
  });

  const [materialItems, setMaterialItems] = useState([]);

  // ================= LOAD DATA =================
  useEffect(() => {
    if (!id) return;

    const loadData = async () => {
      try {
        const [vendorRes, siteRes, materialRes, dmRes] = await Promise.all([
          axiosInstance.get("/showAllvendor"),
          axiosInstance.get("/show-AllProject"),
          axiosInstance.get("/materials"),
          axiosInstance.get(`/DailyMaterial/get/${id}`),
        ]);

        setVendors(vendorRes.data || []);
        setSites(siteRes.data || []);
        setMaterials(materialRes.data || []);

        const data = dmRes.data;
        setDmNo(data.dmNo || "");

        const normalize = (str) => str?.toString().trim().toLowerCase();

        const matchedVendor = vendorRes.data.find(
          (v) => normalize(v.vendorName) === normalize(data.supplierName)
        );

        const matchedSite = siteRes.data.find(
          (s) => normalize(s.name) === normalize(data.siteName)
        );

        setForm({
          vendorId: matchedVendor ? String(matchedVendor.id) : "",
          siteId: matchedSite ? String(matchedSite.id) : "",
          vehicleNo: data.vehicleNo || "",
          remark: data.remark || "",
          engineerName: data.engineerName || user?.email || "",
        });

        setMaterialItems(
          (data.materialItems || []).map((item) => {
            const matchedMaterial = materialRes.data.find(
              (m) =>
                m.id?.toString() === item.materialId?.toString() ||
                m.materialName === item.dailyMaterialName
            );

            return {
              dailyMaterialItemId: item.dailyMaterialItemId || null,
              materialId: matchedMaterial?.id?.toString() || "",
              materialName:
                matchedMaterial?.materialName || item.dailyMaterialName || "",
              unit: item.unit || "",
              size: item.size || "",
              materialQty: item.materialQty?.toString() || "",
              price: item.price?.toString() || "",
              total: item.total?.toString() || "",
            };
          })
        );
      } catch (err) {
        console.error(err);
        setError("Failed to load material details");
      }
    };

    loadData();
  }, [id, user?.email]);

  // ================= MATERIAL HANDLERS =================
  const addRow = () => {
    setMaterialItems((prev) => [
      ...prev,
      {
        dailyMaterialItemId: null,
        materialId: "",
        materialName: "",
        unit: "",
        size: "",
        materialQty: "",
        price: "",
        total: "",
      },
    ]);
  };

  const removeRow = (index) => {
    if (materialItems.length === 1) return;
    setMaterialItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    setMaterialItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        const updated = { ...item, [field]: value };

        if (field === "materialId") {
          const selected = materials.find((m) => m.id.toString() === value);
          updated.materialName = selected?.materialName || "";
        }

        if (canViewPrice && (field === "materialQty" || field === "price")) {
          const qty = Number(
            field === "materialQty" ? value : updated.materialQty || 0
          );
          const price = Number(field === "price" ? value : updated.price || 0);
          updated.total = qty && price ? (qty * price).toFixed(2) : "";
        }

        return updated;
      })
    );
  };

  // ================= SUBMIT =================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.vendorId || !form.siteId) {
      setError("Vendor and site must be selected");
      return;
    }

    const validItems = materialItems.filter(
      (i) => i.materialId && i.unit && i.materialQty
    );

    if (validItems.length !== materialItems.length) {
      setError("Please fill all required material fields");
      return;
    }

    const selectedVendor = vendors.find(
      (v) => v.id.toString() === form.vendorId
    );
    const selectedSite = sites.find((s) => s.id.toString() === form.siteId);

    const payload = {
      vendorId: Number(form.vendorId),
      siteId: Number(form.siteId),
      supplierName: selectedVendor?.vendorName || "",
      siteName: selectedSite?.name || "",
      vehicleNo: form.vehicleNo,
      dmNo,
      remark: form.remark,
      engineerName: form.engineerName,
      materialItems: validItems.map((i) => ({
        ...(i.dailyMaterialItemId && {
          dailyMaterialItemId: i.dailyMaterialItemId,
        }),
        materialId: Number(i.materialId),
        dailyMaterialName: i.materialName,
        unit: i.unit,
        size: i.size || "",
        materialQty: Number(i.materialQty),
        price: Number(i.price || 0),
      })),
    };

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append(
        "data",
        new Blob([JSON.stringify(payload)], {
          type: "application/json",
        })
      );

      if (vehicalImg) formData.append("vehicalImg", vehicalImg);

      await axiosInstance.put(`/DailyMaterial/update/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      navigate(-1);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const grandTotal = materialItems
    .reduce((sum, i) => sum + Number(i.total || 0), 0)
    .toFixed(2);

  // ================= UI =================
  return (
    <div className="am-page">
      <div className="am-card">
        <header className="am-header">
          {dmNo && <span className="am-eyebrow">DM #{dmNo}</span>}
          <h1>Update daily material</h1>
          <p>Edit this delivery entry and its material lines.</p>
        </header>

        {error && (
          <div className="am-alert" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <section className="am-section">
            <h2 className="am-section-title">Delivery details</h2>

            <div className="am-grid">
              <div className="am-field">
                <label htmlFor="vendor">Vendor *</label>
                <select
                  id="vendor"
                  value={form.vendorId}
                  onChange={(e) =>
                    setForm({ ...form, vendorId: e.target.value })
                  }
                  required
                >
                  <option value="">Select vendor</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.vendorName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="am-field">
                <label htmlFor="site">Site *</label>
                <select
                  id="site"
                  value={form.siteId}
                  onChange={(e) =>
                    setForm({ ...form, siteId: e.target.value })
                  }
                  required
                >
                  <option value="">Select site</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="am-field">
                <label htmlFor="vehicleNo">Vehicle number *</label>
                <input
                  id="vehicleNo"
                  placeholder="e.g. MH31 AB 1234"
                  value={form.vehicleNo}
                  onChange={(e) =>
                    setForm({ ...form, vehicleNo: e.target.value })
                  }
                  required
                />
              </div>

              <div className="am-field">
                <label htmlFor="vehicleImg">Vehicle photo</label>
                <label className="am-file" htmlFor="vehicleImg">
                  <UploadCloud size={16} aria-hidden="true" />
                  <span>
                    {vehicalImg ? vehicalImg.name : "Replace photo"}
                  </span>
                </label>
                <input
                  id="vehicleImg"
                  type="file"
                  accept="image/*"
                  className="am-file-input"
                  onChange={(e) => setVehicalImg(e.target.files[0])}
                />
              </div>
            </div>

            <div className="am-field">
              <label htmlFor="remark">Remark</label>
              <textarea
                id="remark"
                rows="3"
                value={form.remark}
                onChange={(e) =>
                  setForm({ ...form, remark: e.target.value })
                }
              />
            </div>
          </section>

          <section className="am-section">
            <div className="am-section-head">
              <h2 className="am-section-title">Materials</h2>
              {canViewPrice && (
                <span className="am-total-pill">
                  Total&nbsp;<strong>₹{grandTotal}</strong>
                </span>
              )}
            </div>

            <div className="am-items">
              {materialItems.map((item, i) => (
                <div
                  className="am-item"
                  key={item.dailyMaterialItemId ?? `new-${i}`}
                >
                  <div className="am-item-grid">
                    <div className="am-field">
                      <label htmlFor={`material-${i}`}>Material</label>
                      <select
                        id={`material-${i}`}
                        value={item.materialId}
                        onChange={(e) =>
                          updateItem(i, "materialId", e.target.value)
                        }
                        required
                      >
                        <option value="">Select material</option>
                        {materials.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.materialName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="am-field">
                      <label htmlFor={`unit-${i}`}>Unit</label>
                      <input
                        id={`unit-${i}`}
                        value={item.unit}
                        onChange={(e) =>
                          updateItem(i, "unit", e.target.value)
                        }
                        required
                      />
                    </div>

                    <div className="am-field">
                      <label htmlFor={`size-${i}`}>Size</label>
                      <input
                        id={`size-${i}`}
                        value={item.size}
                        onChange={(e) =>
                          updateItem(i, "size", e.target.value)
                        }
                      />
                    </div>

                    <div className="am-field">
                      <label htmlFor={`qty-${i}`}>Quantity</label>
                      <input
                        id={`qty-${i}`}
                        type="number"
                        value={item.materialQty}
                        onChange={(e) =>
                          updateItem(i, "materialQty", e.target.value)
                        }
                        required
                      />
                    </div>

                    {canViewPrice && (
                      <div className="am-field">
                        <label htmlFor={`price-${i}`}>Price</label>
                        <input
                          id={`price-${i}`}
                          type="number"
                          value={item.price}
                          onChange={(e) =>
                            updateItem(i, "price", e.target.value)
                          }
                        />
                      </div>
                    )}

                    {canViewPrice && (
                      <div className="am-field am-field-static">
                        <span className="am-static-label">Total</span>
                        <span className="am-static-value">
                          ₹{item.total || 0}
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="am-remove"
                    onClick={() => removeRow(i)}
                    disabled={materialItems.length === 1}
                    aria-label="Remove this material"
                  >
                    <Trash2 size={16} aria-hidden="true" />
                    <span>Remove</span>
                  </button>
                </div>
              ))}
            </div>

            <button type="button" className="am-add-row" onClick={addRow}>
              <Plus size={16} aria-hidden="true" /> Add another material
            </button>
          </section>

          <div className="am-footer">
            <button type="submit" className="am-submit" disabled={loading}>
              {loading ? "Updating…" : "Update entry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}